import React, { useEffect, useReducer, useCallback } from 'react';
import GridComponent from './GridComponent';
import { formatCurrency } from '../utils/currencyUtils';
import { systemBus, EVENTS } from '../services/systemBus';
import { Z } from '../constants/zLayers';
import useIsMobile from '../hooks/useIsMobile';
import { useCharacter } from '../contexts/CharacterContext';
import { useModalEscape } from '../hooks/useModalEscape';

const VaultModal = ({
  showVaultModal,
  setShowVaultModal,
  vaults,
  vaultGridSize,
  setEditingVault,
  gridCols,
  gridRows,
  inventory,
  draggedItem,
  hoveredGrid,
  hoveredCell,
  // Tap-to-move state (added MAR 2026 for tap mode support)
  selectedItem,
  setSelectedItem,
  selectedItemSource,
  setSelectedItemSource,
  isValidPosition,
  handleMouseDown,
  handleContextMenu,
  showStatus,
  setOpenContainer,
  setEditingItem,
  settings,
  InventoryItem,
  setShowItemDialog,
  setDirectAddToVault
}) => {
  // [SYSTEM UNIFICATION - OCT 25, 2025]: Force re-render mechanism for event subscriptions
  // PURPOSE: Ensures vault weight and contents update when vault items change
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const { logActivity } = useCharacter();

  // [APR 2026]: Standardize Escape dismissal via useModalEscape hook.
  // [WHY]: Wrap setShowVaultModal(null) for a stable callback ref.
  const handleCloseVault = useCallback(() => setShowVaultModal(null), [setShowVaultModal]);
  useModalEscape(Boolean(showVaultModal), handleCloseVault);

  // [SYSTEM UNIFICATION - OCT 25, 2025]: Event subscriptions for reactive vault updates
  // PURPOSE: Vault modal re-renders when items are transferred to/from vaults
  // WHY: Ensures vault weight display (line 44) stays accurate when vault contents change
  // INTEGRATION: Complements GridComponent subscriptions, handles vault-specific weight calculation
  useEffect(() => {
    const handleVaultChange = (eventName, data) => {
      forceUpdate();

      // [FIX APR 2026]: Log meaningful vault deposit/withdrawal actions
      if (eventName === 'INVENTORY_VAULT_TRANSFER' && data?.item) {
        const currentVault = vaults.find(v => v.id === showVaultModal);
        const vaultName = currentVault?.name || 'vault';
        if (data.direction === 'to_vault' || data.toVault) {
          logActivity(`Deposited ${data.item.name} into ${vaultName}`, 'inventory', {
            itemName: data.item.name, vaultName, direction: 'deposit'
          });
        } else if (data.direction === 'from_vault' || data.fromVault) {
          logActivity(`Withdrew ${data.item.name} from ${vaultName}`, 'inventory', {
            itemName: data.item.name, vaultName, direction: 'withdraw'
          });
        }
      }
    };

    // Subscribe to vault-affecting events
    const unsubscribeVaultTransfer = systemBus.on(EVENTS.INVENTORY_VAULT_TRANSFER, (data) =>
      handleVaultChange('INVENTORY_VAULT_TRANSFER', data));
    const unsubscribeItemAdded = systemBus.on(EVENTS.INVENTORY_ITEM_ADDED, (data) =>
      handleVaultChange('INVENTORY_ITEM_ADDED', data));
    const unsubscribeItemRemoved = systemBus.on(EVENTS.INVENTORY_ITEM_REMOVED, (data) =>
      handleVaultChange('INVENTORY_ITEM_REMOVED', data));
    const unsubscribeCurrencyChanged = systemBus.on(EVENTS.CURRENCY_CHANGED, (data) =>
      handleVaultChange('CURRENCY_CHANGED', data));

    // Cleanup: Unsubscribe on component unmount to prevent memory leaks
    return () => {
      unsubscribeVaultTransfer();
      unsubscribeItemAdded();
      unsubscribeItemRemoved();
      unsubscribeCurrencyChanged();
    };
  }, [showVaultModal, vaults, logActivity]); // Re-subscribe when active vault changes

  // [HOOKS ORDER FIX — APR 2026 Phase 3A.1]: All hooks must run on every render.
  // [WHY]: Previously useIsMobile() was called AFTER early returns below, causing
  //        React error #310 (rendered more hooks than during previous render) when
  //        the Manage/Open-vault button triggered the first render with a valid vault.
  const isMobile = useIsMobile();
  const useTapMode = isMobile && settings?.mobileInteractionMode === 'tap';

  if (!showVaultModal) return null;
  const vault = vaults.find(v => v.id === showVaultModal);
  if (!vault) return null;

  const vaultCols = (vault.gridSize && vault.gridSize[0]) || vaultGridSize[0];
  const vaultRows = (vault.gridSize && vault.gridSize[1]) || vaultGridSize[1];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90" data-testid="vault-modal" style={{ backgroundColor: 'rgba(0,0,0,0.95)', zIndex: Z.VAULT_MODAL }}>
      <div className="fixed inset-4 bg-gray-900 rounded-lg flex flex-col">
        <div className="bg-gray-800 p-3 border-b-2 border-gray-700 flex justify-between items-center rounded-t-lg">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">📦 {vault.name}</h2>
            <p className="text-xs text-gray-400">
              📍 {vault.location} 
              {vault.cost > 0 && ` | 💰 ${formatCurrency(vault.cost)}`}
              {` | ${(vault.items?.reduce((sum, i) => sum + (i.weight || 0), 0) || 0).toFixed(1)}lb`}
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => {
                setDirectAddToVault(vault.id);
                setShowItemDialog(true);
              }}
              className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Add Item
            </button>
            <button 
              onClick={() => setEditingVault(vault.id)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Edit
            </button>
            <button 
              onClick={() => setShowVaultModal(null)}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GridComponent
              gridId="inventory"
              cols={gridCols}
              rows={gridRows}
              items={inventory}
              title={`Inventory (${gridCols}x${gridRows})`}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              selectedItemSource={selectedItemSource}
              setSelectedItemSource={setSelectedItemSource}
              isValidPosition={isValidPosition}
              handleMouseDown={handleMouseDown}
              handleContextMenu={handleContextMenu}
              setOpenContainer={setOpenContainer}
              setEditingItem={setEditingItem}
              InventoryItem={InventoryItem}
              enableDragDrop={!useTapMode}
            />

            <GridComponent
              gridId={vault.id}
              cols={vaultCols}
              rows={vaultRows}
              items={vault.items || []}
              title={`Vault Storage (${vaultCols}x${vaultRows})`}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              selectedItemSource={selectedItemSource}
              setSelectedItemSource={setSelectedItemSource}
              isValidPosition={isValidPosition}
              handleMouseDown={handleMouseDown}
              handleContextMenu={handleContextMenu}
              setOpenContainer={setOpenContainer}
              setEditingItem={setEditingItem}
              InventoryItem={InventoryItem}
              enableDragDrop={!useTapMode}
            />
          </div>
        </div>
        
        <div className="p-3 bg-gray-800 text-sm text-gray-400 rounded-b-lg border-t border-gray-700">
          {'\uD83D\uDCA1'} {useTapMode
            ? 'Tap items to select, then tap empty cells to move them'
            : 'Drag items between grids'
          } {'\u2022'} Items in vaults don't count toward carry weight
          <br />
          ⚠️ Containers can be stored in vaults but cannot be opened - move to inventory first to access contents
        </div>
      </div>
    </div>
  );
};

export default VaultModal;